import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, MessageCircle, Phone, X } from "lucide-react";
import { telLink, waLink } from "../data/products";
import { useLang } from "../i18n";
import QuoteModal, { openQuote } from "./QuoteForm";
import { EASE, Micro, useHashNav } from "./Reveal";

const HASHES = [
  { key: "about", hash: "#about" },
  { key: "services", hash: "#services" },
  { key: "products", hash: "#products" },
  { key: "catalogue", route: "/catalogue" },
  { key: "projects", hash: "#projects" },
  { key: "journal", hash: "#journal" },
  { key: "contact", hash: "#contact" },
];

export function LangToggle({ mobile }) {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/15" data-testid={mobile ? "mobile-lang-toggle" : "nav-lang-toggle"}>
      {["en", "ta"].map((l) => (
        <button
          key={l}
          data-testid={`${mobile ? "mobile" : "nav"}-lang-${l}`}
          onClick={() => setLang(l)}
          aria-label={l === "en" ? "Switch to English" : "Switch to Tamil"}
          className={`px-2.5 py-1.5 text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${
            lang === l ? "bg-gold text-ink font-bold" : "text-cream/55 hover:text-cream"
          }`}
        >
          {l === "en" ? "EN" : "தமிழ்"}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const go = useHashNav();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAndClose = (l) => {
    setOpen(false);
    setTimeout(() => (l.route ? navigate(l.route) : go(l.hash)), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        className="fixed top-0 inset-x-0 z-[100] px-4 md:px-8"
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: pathname === "/" ? 1.9 : 0.7, ease: EASE }}
      >
        <div
          data-testid="navbar"
          className={`flex items-center justify-between transition-all duration-500 ${
            scrolled
              ? "mt-3 mx-auto max-w-6xl px-4 md:px-6 py-2.5 bg-ink/80 backdrop-blur-md border border-white/10"
              : "mt-0 px-0 md:px-2 py-5 border border-transparent"
          }`}
        >
          <Link to="/" data-testid="nav-logo" data-cursor="link" aria-label="INT–EXT home" className="flex items-center gap-3">
            <img
              src="/logo-mark.png"
              alt="INT–EXT G mark"
              className={`mix-blend-screen transition-all duration-500 ${scrolled ? "h-8" : "h-10 md:h-12"}`}
            />
            <span className="leading-none">
              <span className={`block font-display text-gold tracking-[0.12em] whitespace-nowrap transition-all duration-500 ${scrolled ? "text-base" : "text-lg md:text-xl"}`}>
                INT–EXT
              </span>
              {!scrolled && (
                <span className="hidden md:block text-[8px] tracking-[0.32em] text-cream/45 uppercase mt-1 whitespace-nowrap">
                  {t("nav.brand")}
                </span>
              )}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {HASHES.map((l) => (
              <button
                key={l.key}
                data-testid={`nav-link-${l.key}`}
                data-cursor="link"
                onClick={() => (l.route ? navigate(l.route) : go(l.hash))}
                className="relative text-[10px] tracking-[0.25em] uppercase text-cream/60 hover:text-cream transition-colors duration-300 group"
              >
                {t(`nav.${l.key}`)}
                <span className="absolute -bottom-1 left-0 h-px w-full bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <LangToggle />
            <button
              data-testid="nav-quote"
              data-cursor="link"
              onClick={openQuote}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-gold hover:text-goldbright transition-colors border-b border-gold/40 pb-1"
            >
              {t("nav.quote")} <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden text-cream p-1"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[140] bg-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-5 py-5">
              <div className="flex items-center gap-3">
                <img src="/logo-mark.png" alt="INT–EXT" className="h-10 mix-blend-screen" />
                <span className="font-display text-gold text-lg tracking-[0.12em]">INT–EXT</span>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-cream p-1"
                aria-label="Close menu"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-1 overflow-y-auto">
              {HASHES.map((l, i) => (
                <span key={l.key} className="block overflow-hidden">
                  <motion.button
                    data-testid={`mobile-nav-${l.key}`}
                    onClick={() => goAndClose(l)}
                    className="block font-display uppercase text-3xl py-2 text-cream/90 text-left hover:text-gold transition-colors"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.55, delay: 0.06 * i, ease: EASE }}
                  >
                    {t(`nav.${l.key}`)}
                  </motion.button>
                </span>
              ))}
            </nav>
            <div className="px-6 pb-10 flex flex-col items-start gap-3">
              <LangToggle mobile />
              <a
                href={telLink}
                data-testid="mobile-phone"
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase border border-white/20 px-5 py-3 text-cream/80"
              >
                {t("nav.callUs")} <Phone className="w-3.5 h-3.5" />
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp"
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase border border-gold/40 px-5 py-3 text-gold"
              >
                {t("nav.whatsapp")} <MessageCircle className="w-3.5 h-3.5" />
              </a>
                <button
                data-testid="mobile-quote"
                onClick={() => {
                  setOpen(false);
                  setTimeout(openQuote, 350);
                }}
                className="text-[10px] tracking-[0.25em] uppercase bg-gold text-ink font-bold px-5 py-3"
              >
                {t("nav.quote")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <QuoteModal />
    </>
  );
}
